import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import api from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Settings2,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ClipboardList,
  Calendar as CalendarIcon,
  Loader2
} from 'lucide-react';

// Shadcn UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";

const ProductGovernanceMatrixPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Main state
  const [items, setItems] = useState([]);
  const [dropdowns, setDropdowns] = useState({
    people: [],
    subject: [],
    type: [],
    status: [],
    priority: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  
  // Dialog states
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [showDropdownDialog, setShowDropdownDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  
  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    start_date: null,
    from_value: '',
    to_value: '',
    subject: '',
    item_type: '',
    status: '',
    priority: '',
    is_ok: false,
    closure_date: null,
    consider_corrections: '',
    observations: ''
  });
  
  // Dropdown management state
  const [activeDropdownTab, setActiveDropdownTab] = useState('status');
  const [newDropdownValue, setNewDropdownValue] = useState('');
  const [editingDropdown, setEditingDropdown] = useState(null);
  const [editDropdownValue, setEditDropdownValue] = useState('');

  // Fetch items and dropdowns
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch items and dropdowns in parallel (no auto-seeding)
      const [itemsRes, dropdownsRes, sortPrefRes] = await Promise.all([
        api.get('/product-governance'),
        api.get('/product-governance/dropdowns'),
        api.get('/product-governance/sort-preference')
      ]);
      
      setItems(itemsRes.data || []);
      
      // Organize dropdowns by field type (merge from/to into people)
      const organizedDropdowns = {
        people: [],
        subject: [],
        type: [],
        status: [],
        priority: []
      };
      
      (dropdownsRes.data || []).forEach(d => {
        // Merge from and to into people
        if (d.field_type === 'from' || d.field_type === 'to' || d.field_type === 'people') {
          // Check if this value already exists in people (to avoid duplicates)
          const exists = organizedDropdowns.people.some(p => p.value === d.value);
          if (!exists) {
            organizedDropdowns.people.push({ ...d, field_type: 'people' });
          }
        } else if (organizedDropdowns[d.field_type]) {
          organizedDropdowns[d.field_type].push(d);
        }
      });
      
      setDropdowns(organizedDropdowns);
      
      // Set sort preference
      if (sortPrefRes.data?.sort_column) {
        setSortConfig({
          column: sortPrefRes.data.sort_column,
          direction: sortPrefRes.data.sort_direction || 'asc'
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error_loading_data', 'common') || 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sort items
  const sortedItems = React.useMemo(() => {
    if (!sortConfig.column) return items;
    
    return [...items].sort((a, b) => {
      let aVal = a[sortConfig.column];
      let bVal = b[sortConfig.column];
      
      // Handle display values for dropdowns
      if (sortConfig.column.endsWith('_display')) {
        aVal = a[sortConfig.column] || '';
        bVal = b[sortConfig.column] || '';
      }
      
      // Handle dates
      if (sortConfig.column === 'start_date' || sortConfig.column === 'closure_date' || sortConfig.column === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // Handle booleans
      if (sortConfig.column === 'is_ok') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      }
      
      // Handle strings
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortConfig]);

  // Handle sort
  const handleSort = async (column) => {
    let newDirection = 'asc';
    if (sortConfig.column === column) {
      newDirection = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ column, direction: newDirection });
    
    // Save sort preference to API
    try {
      await api.put('/product-governance/sort-preference', {
        sort_column: column,
        sort_direction: newDirection
      });
    } catch (error) {
      console.error('Error saving sort preference:', error);
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortConfig.column !== column) {
      return <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-4 w-4" />
      : <ChevronDown className="h-4 w-4" />;
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      start_date: null,
      from_value: '',
      to_value: '',
      subject: '',
      item_type: '',
      status: '',
      priority: '',
      is_ok: false,
      closure_date: null,
      consider_corrections: '',
      observations: ''
    });
    setEditingItem(null);
  };

  // Open add dialog
  const handleAdd = () => {
    resetForm();
    setShowAddEditDialog(true);
  };

  // Open edit dialog
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      start_date: item.start_date ? new Date(item.start_date) : null,
      from_value: item.from_value || '',
      to_value: item.to_value || '',
      subject: item.subject || '',
      item_type: item.item_type || '',
      status: item.status || '',
      priority: item.priority || '',
      is_ok: item.is_ok || false,
      closure_date: item.closure_date ? new Date(item.closure_date) : null,
      consider_corrections: item.consider_corrections || '',
      observations: item.observations || ''
    });
    setShowAddEditDialog(true);
  };

  // Save item (create or update)
  const handleSaveItem = async () => {
    // Name is no longer required
    
    try {
      setSaving(true);
      
      const payload = {
        name: formData.name?.trim() || '',
        start_date: formData.start_date ? formData.start_date.toISOString() : null,
        from_value: formData.from_value || null,
        to_value: formData.to_value || null,
        subject: formData.subject || null,
        item_type: formData.item_type || null,
        status: formData.status || null,
        priority: formData.priority || null,
        is_ok: formData.is_ok,
        closure_date: formData.closure_date ? formData.closure_date.toISOString() : null,
        consider_corrections: formData.consider_corrections || null,
        observations: formData.observations || null
      };
      
      if (editingItem) {
        await api.put(`/product-governance/${editingItem.id}`, payload);
        toast.success(t('item_updated', 'product_governance') || 'Item updated successfully');
      } else {
        await api.post('/product-governance', payload);
        toast.success(t('item_created', 'product_governance') || 'Item created successfully');
      }
      
      setShowAddEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(t('error_saving', 'common') || 'Error saving item');
    } finally {
      setSaving(false);
    }
  };

  // Delete item
  const handleDelete = async () => {
    if (!deletingItem) return;
    
    try {
      setSaving(true);
      await api.delete(`/product-governance/${deletingItem.id}`);
      toast.success(t('item_deleted', 'product_governance') || 'Item deleted successfully');
      setShowDeleteDialog(false);
      setDeletingItem(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(t('error_deleting', 'common') || 'Error deleting item');
    } finally {
      setSaving(false);
    }
  };

  // Toggle is_ok directly in table
  const handleToggleOk = async (item) => {
    try {
      await api.put(`/product-governance/${item.id}`, { is_ok: !item.is_ok });
      fetchData();
    } catch (error) {
      console.error('Error toggling Ok:', error);
      toast.error(t('error_updating', 'common') || 'Error updating item');
    }
  };

  // Add dropdown value
  const handleAddDropdown = async () => {
    if (!newDropdownValue.trim()) return;
    
    try {
      setSaving(true);
      // For 'people' tab, store as 'from' field_type in backend (shared between from/to)
      const backendFieldType = activeDropdownTab === 'people' ? 'from' : activeDropdownTab;
      await api.post('/product-governance/dropdowns', {
        field_type: backendFieldType,
        value: newDropdownValue.trim(),
        sort_order: dropdowns[activeDropdownTab].length + 1
      });
      toast.success(t('dropdown_added', 'product_governance') || 'Option added successfully');
      setNewDropdownValue('');
      fetchData();
    } catch (error) {
      console.error('Error adding dropdown:', error);
      if (error.response?.data?.detail?.includes('already exists')) {
        toast.error(t('dropdown_exists', 'product_governance') || 'This option already exists');
      } else {
        toast.error(t('error_adding', 'common') || 'Error adding option');
      }
    } finally {
      setSaving(false);
    }
  };

  // Update dropdown value
  const handleUpdateDropdown = async (dropdownId) => {
    if (!editDropdownValue.trim()) return;
    
    try {
      setSaving(true);
      await api.put(`/product-governance/dropdowns/${dropdownId}`, {
        value: editDropdownValue.trim()
      });
      toast.success(t('dropdown_updated', 'product_governance') || 'Option updated successfully');
      setEditingDropdown(null);
      setEditDropdownValue('');
      fetchData();
    } catch (error) {
      console.error('Error updating dropdown:', error);
      toast.error(t('error_updating', 'common') || 'Error updating option');
    } finally {
      setSaving(false);
    }
  };

  // Delete dropdown value
  const handleDeleteDropdown = async (dropdownId) => {
    try {
      setSaving(true);
      await api.delete(`/product-governance/dropdowns/${dropdownId}`);
      toast.success(t('dropdown_deleted', 'product_governance') || 'Option deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting dropdown:', error);
      toast.error(t('error_deleting', 'common') || 'Error deleting option');
    } finally {
      setSaving(false);
    }
  };

  // Move dropdown up in order
  const handleMoveUp = async (dropdownId) => {
    const currentList = dropdowns[activeDropdownTab];
    const currentIndex = currentList.findIndex(d => d.id === dropdownId);
    
    if (currentIndex <= 0) return; // Already at top
    
    const currentItem = currentList[currentIndex];
    const previousItem = currentList[currentIndex - 1];
    
    try {
      setSaving(true);
      // Swap sort_order values
      await Promise.all([
        api.put(`/product-governance/dropdowns/${currentItem.id}`, { sort_order: previousItem.sort_order }),
        api.put(`/product-governance/dropdowns/${previousItem.id}`, { sort_order: currentItem.sort_order })
      ]);
      toast.success(t('reorder_success', 'product_governance') || 'Order updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error moving dropdown up:', error);
      toast.error(t('error_reorder', 'product_governance') || 'Error updating order');
    } finally {
      setSaving(false);
    }
  };

  // Move dropdown down in order
  const handleMoveDown = async (dropdownId) => {
    const currentList = dropdowns[activeDropdownTab];
    const currentIndex = currentList.findIndex(d => d.id === dropdownId);
    
    if (currentIndex < 0 || currentIndex >= currentList.length - 1) return; // Already at bottom
    
    const currentItem = currentList[currentIndex];
    const nextItem = currentList[currentIndex + 1];
    
    try {
      setSaving(true);
      // Swap sort_order values
      await Promise.all([
        api.put(`/product-governance/dropdowns/${currentItem.id}`, { sort_order: nextItem.sort_order }),
        api.put(`/product-governance/dropdowns/${nextItem.id}`, { sort_order: currentItem.sort_order })
      ]);
      toast.success(t('reorder_success', 'product_governance') || 'Order updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error moving dropdown down:', error);
      toast.error(t('error_reorder', 'product_governance') || 'Error updating order');
    } finally {
      setSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yy');
    } catch {
      return '-';
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'medium': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'low': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'in progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'to do': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
      case 'on hold': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Dropdown field labels (merged from/to into people/cuentas)
  const dropdownLabels = {
    people: t('people', 'product_governance') || 'People',
    subject: t('subject', 'product_governance') || 'Subject',
    type: t('type', 'product_governance') || 'Type',
    status: t('status', 'product_governance') || 'Status',
    priority: t('priority', 'product_governance') || 'Priority'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6" data-testid="product-governance-loading">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" data-testid="product-governance-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ClipboardList className="h-6 w-6 text-primary" />
                {t('title', 'product_governance') || 'Product Governance Matrix'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {t('description', 'product_governance') || 'Centralized master table to organize, prioritize, and track all product requirements, improvements, and issues.'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDropdownDialog(true)}
              data-testid="manage-dropdowns-btn"
            >
              <Settings2 className="h-4 w-4 mr-2" />
              {t('manage_options', 'product_governance') || 'Manage Options'}
            </Button>
            <Button onClick={handleAdd} data-testid="add-entry-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('add_entry', 'product_governance') || 'Add Entry'}
            </Button>
          </div>
        </div>

        {/* Main Table Card */}
        <Card data-testid="governance-table-card">
          <CardContent className="p-0 overflow-x-auto">
            <div className="w-full">
              <Table className="w-full table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors w-[100px]"
                        onClick={() => handleSort('start_date')}
                      >
                        <div className="flex items-center gap-1">
                          {t('start_date', 'product_governance') || 'Start Date'}
                          {getSortIcon('start_date')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSort('from_display')}
                      >
                        <div className="flex items-center gap-1">
                          {t('from', 'product_governance') || 'From'}
                          {getSortIcon('from_display')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSort('to_display')}
                      >
                        <div className="flex items-center gap-1">
                          {t('to', 'product_governance') || 'To'}
                          {getSortIcon('to_display')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSort('subject_display')}
                      >
                        <div className="flex items-center gap-1">
                          {t('subject', 'product_governance') || 'Subject'}
                          {getSortIcon('subject_display')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSort('item_type_display')}
                      >
                        <div className="flex items-center gap-1">
                          {t('type', 'product_governance') || 'Type'}
                          {getSortIcon('item_type_display')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors min-w-[200px]"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          {t('name', 'product_governance') || 'Name'}
                          {getSortIcon('name')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSort('status_display')}
                      >
                        <div className="flex items-center gap-1">
                          {t('status', 'product_governance') || 'Status'}
                          {getSortIcon('status_display')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSort('priority_display')}
                      >
                        <div className="flex items-center gap-1">
                          {t('priority', 'product_governance') || 'Priority'}
                          {getSortIcon('priority_display')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors w-[60px] text-center"
                        onClick={() => handleSort('is_ok')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {t('ok', 'product_governance') || 'Ok'}
                          {getSortIcon('is_ok')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors w-[100px]"
                        onClick={() => handleSort('closure_date')}
                      >
                        <div className="flex items-center gap-1">
                          {t('closure_date', 'product_governance') || 'Closure'}
                          {getSortIcon('closure_date')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors w-[15%]"
                        onClick={() => handleSort('consider_corrections')}
                      >
                        <div className="flex items-center gap-1">
                          {t('consider_corrections', 'product_governance') || 'Consider/Corrections'}
                          {getSortIcon('consider_corrections')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 transition-colors w-[15%]"
                        onClick={() => handleSort('observations')}
                      >
                        <div className="flex items-center gap-1">
                          {t('observations', 'product_governance') || 'Observations'}
                          {getSortIcon('observations')}
                        </div>
                      </TableHead>
                      <TableHead className="w-[80px] text-center">
                        {t('actions', 'common') || 'Actions'}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                          <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          {t('no_entries', 'product_governance') || 'No entries found. Click "Add Entry" to create one.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedItems.map((item) => (
                        <TableRow key={item.id} data-testid={`row-${item.id}`}>
                          <TableCell className="font-mono text-sm">
                            {formatDate(item.start_date)}
                          </TableCell>
                          <TableCell>{item.from_display || '-'}</TableCell>
                          <TableCell>{item.to_display || '-'}</TableCell>
                          <TableCell>{item.subject_display || '-'}</TableCell>
                          <TableCell>
                            {item.item_type_display && (
                              <Badge variant="outline" className="font-normal">
                                {item.item_type_display}
                              </Badge>
                            )}
                            {!item.item_type_display && '-'}
                          </TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate" title={item.name}>
                            {item.name || '-'}
                          </TableCell>
                          <TableCell>
                            {item.status_display && (
                              <Badge className={`font-normal ${getStatusColor(item.status_display)}`}>
                                {item.status_display}
                              </Badge>
                            )}
                            {!item.status_display && '-'}
                          </TableCell>
                          <TableCell>
                            {item.priority_display && (
                              <Badge className={`font-normal ${getPriorityColor(item.priority_display)}`}>
                                {item.priority_display}
                              </Badge>
                            )}
                            {!item.priority_display && '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              checked={item.is_ok}
                              onCheckedChange={() => handleToggleOk(item)}
                              data-testid={`ok-checkbox-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatDate(item.closure_date)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-pre-wrap break-words align-top">
                            {item.consider_corrections || '-'}
                          </TableCell>
                          <TableCell className="text-sm whitespace-pre-wrap break-words align-top">
                            {item.observations || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(item)}
                                data-testid={`edit-btn-${item.id}`}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setDeletingItem(item);
                                  setShowDeleteDialog(true);
                                }}
                                data-testid={`delete-btn-${item.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="text-sm text-muted-foreground text-center">
          {t('total_entries', 'product_governance') || 'Total entries'}: {items.length}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddEditDialog} onOpenChange={setShowAddEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem 
                ? (t('edit_entry', 'product_governance') || 'Edit Entry')
                : (t('add_entry', 'product_governance') || 'Add Entry')
              }
            </DialogTitle>
            <DialogDescription>
              {t('fill_details', 'product_governance') || 'Fill in the details for this governance item.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">{t('name', 'product_governance') || 'Name'}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('name_placeholder', 'product_governance') || 'Enter item name...'}
                data-testid="input-name"
              />
            </div>
            
            {/* Row 1: Start Date, Closure Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('start_date', 'product_governance') || 'Start Date'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="justify-start text-left font-normal"
                      data-testid="input-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.start_date 
                        ? format(formData.start_date, 'dd/MM/yyyy')
                        : (t('select_date', 'product_governance') || 'Select date')
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData({ ...formData, start_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="grid gap-2">
                <Label>{t('closure_date', 'product_governance') || 'Closure Date'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="justify-start text-left font-normal"
                      data-testid="input-closure-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.closure_date 
                        ? format(formData.closure_date, 'dd/MM/yyyy')
                        : (t('select_date', 'product_governance') || 'Select date')
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.closure_date}
                      onSelect={(date) => setFormData({ ...formData, closure_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            {/* Row 2: From, To - Both use People dropdown */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('from', 'product_governance') || 'From'}</Label>
                <Select
                  value={formData.from_value}
                  onValueChange={(value) => setFormData({ ...formData, from_value: value })}
                >
                  <SelectTrigger data-testid="select-from">
                    <SelectValue placeholder={t('select_option', 'product_governance') || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.people
                      .filter(d => d.id !== formData.to_value) // Exclude selected "To" value
                      .map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.value}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label>{t('to', 'product_governance') || 'To'}</Label>
                <Select
                  value={formData.to_value}
                  onValueChange={(value) => setFormData({ ...formData, to_value: value })}
                >
                  <SelectTrigger data-testid="select-to">
                    <SelectValue placeholder={t('select_option', 'product_governance') || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.people
                      .filter(d => d.id !== formData.from_value) // Exclude selected "From" value
                      .map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.value}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 3: Subject, Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('subject', 'product_governance') || 'Subject'}</Label>
                <Select
                  value={formData.subject}
                  onValueChange={(value) => setFormData({ ...formData, subject: value })}
                >
                  <SelectTrigger data-testid="select-subject">
                    <SelectValue placeholder={t('select_option', 'product_governance') || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.subject.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label>{t('type', 'product_governance') || 'Type'}</Label>
                <Select
                  value={formData.item_type}
                  onValueChange={(value) => setFormData({ ...formData, item_type: value })}
                >
                  <SelectTrigger data-testid="select-type">
                    <SelectValue placeholder={t('select_option', 'product_governance') || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.type.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 4: Status, Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('status', 'product_governance') || 'Status'}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder={t('select_option', 'product_governance') || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.status.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label>{t('priority', 'product_governance') || 'Priority'}</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid="select-priority">
                    <SelectValue placeholder={t('select_option', 'product_governance') || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.priority.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Is Ok Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_ok"
                checked={formData.is_ok}
                onCheckedChange={(checked) => setFormData({ ...formData, is_ok: checked })}
                data-testid="checkbox-is-ok"
              />
              <Label htmlFor="is_ok" className="cursor-pointer">
                {t('mark_as_ok', 'product_governance') || 'Mark as Ok / Resolved'}
              </Label>
            </div>
            
            {/* Consider/Corrections */}
            <div className="grid gap-2">
              <Label>{t('consider_corrections', 'product_governance') || 'Consider / Corrections'}</Label>
              <Textarea
                value={formData.consider_corrections}
                onChange={(e) => setFormData({ ...formData, consider_corrections: e.target.value })}
                placeholder={t('consider_placeholder', 'product_governance') || 'Notes from creator...'}
                rows={2}
                data-testid="textarea-corrections"
              />
            </div>
            
            {/* Observations */}
            <div className="grid gap-2">
              <Label>{t('observations', 'product_governance') || 'Observations'}</Label>
              <Textarea
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                placeholder={t('observations_placeholder', 'product_governance') || 'General observations...'}
                rows={2}
                data-testid="textarea-observations"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEditDialog(false)}>
              {t('cancel', 'common') || 'Cancel'}
            </Button>
            <Button onClick={handleSaveItem} disabled={saving} data-testid="save-item-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem 
                ? (t('update', 'common') || 'Update')
                : (t('create', 'common') || 'Create')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dropdown Management Dialog */}
      <Dialog open={showDropdownDialog} onOpenChange={setShowDropdownDialog}>
        <DialogContent className="max-w-lg" data-testid="dropdown-management-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('manage_options', 'product_governance') || 'Manage Dropdown Options'}
            </DialogTitle>
            <DialogDescription>
              {t('manage_options_desc', 'product_governance') || 'Add, edit, or remove options for dropdown fields.'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeDropdownTab} onValueChange={setActiveDropdownTab}>
            <TabsList className="grid grid-cols-3 lg:grid-cols-6">
              {Object.keys(dropdownLabels).map((key) => (
                <TabsTrigger key={key} value={key} className="text-xs px-2">
                  {dropdownLabels[key]}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {Object.keys(dropdownLabels).map((fieldType) => (
              <TabsContent key={fieldType} value={fieldType} className="mt-4">
                {/* Add new value */}
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder={`${t('add_new', 'common') || 'Add new'} ${dropdownLabels[fieldType]}...`}
                    value={newDropdownValue}
                    onChange={(e) => setNewDropdownValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddDropdown()}
                    data-testid={`input-new-${fieldType}`}
                  />
                  <Button onClick={handleAddDropdown} disabled={saving || !newDropdownValue.trim()}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* List of values */}
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {dropdowns[fieldType].length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">
                        {t('no_options', 'product_governance') || 'No options yet. Add one above.'}
                      </p>
                    ) : (
                      dropdowns[fieldType].map((dropdown, index) => (
                        <div
                          key={dropdown.id}
                          className="flex items-center gap-2 p-2 rounded-md border bg-card"
                          data-testid={`dropdown-item-${dropdown.id}`}
                        >
                          {/* Position number */}
                          <span className="w-8 text-center text-sm font-medium text-muted-foreground bg-muted rounded px-1">
                            #{index + 1}
                          </span>
                          
                          {editingDropdown === dropdown.id ? (
                            <>
                              <Input
                                value={editDropdownValue}
                                onChange={(e) => setEditDropdownValue(e.target.value)}
                                className="flex-1"
                                autoFocus
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleUpdateDropdown(dropdown.id)}
                                disabled={saving}
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  setEditingDropdown(null);
                                  setEditDropdownValue('');
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <span className="flex-1 font-medium">{dropdown.value}</span>
                              
                              {/* Move Up/Down buttons */}
                              <div className="flex items-center gap-0.5">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => handleMoveUp(dropdown.id)}
                                  disabled={saving || index === 0}
                                  title={t('move_up', 'product_governance') || 'Move Up'}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => handleMoveDown(dropdown.id)}
                                  disabled={saving || index === dropdowns[fieldType].length - 1}
                                  title={t('move_down', 'product_governance') || 'Move Down'}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {/* Edit button */}
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => {
                                  setEditingDropdown(dropdown.id);
                                  setEditDropdownValue(dropdown.value);
                                }}
                                title={t('edit', 'common') || 'Edit'}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              
                              {/* Delete button */}
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => handleDeleteDropdown(dropdown.id)}
                                disabled={saving}
                                title={t('delete', 'common') || 'Delete'}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDropdownDialog(false)}>
              {t('close', 'common') || 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('confirm_delete', 'product_governance') || 'Confirm Delete'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_warning', 'product_governance') || 'Are you sure you want to delete this entry? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel', 'common') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('delete', 'common') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductGovernanceMatrixPage;
